/**
 * تنها کنترلِ صدای زنده سرِ میز: دکمه‌ی گردِ بی‌صدا/باصدا در گوشه‌ی پایین-چپ.
 * همتای VoiceMuteButton ِ اپ. (تصمیمِ کاربر، سپتامبر ۲۰۲۶)
 *
 * صدا پیوسته است: بارِ اولی که میزی صدا دارد، کارتِ «صدای زنده‌ی میز» کنارِ همین
 * دکمه می‌پرسد و پاسخ در [[voice]] می‌مانَد. با «اجازه می‌دهم» میکروفون تا پایانِ
 * میز باز است و صدای بقیه همیشه پخش می‌شود. این دکمه فقط میکروفونِ خودم را
 * می‌بندد و باز می‌کند:
 *   • سبز با میکروفون = باز (هاله‌ی فیروزه‌ای وقتی حرف می‌زنم).
 *   • سرخ با خطِ مورب = بی‌صدا، اجازه نداده، یا مرورگر اجازه نداده — لمسش در دو
 *     حالتِ آخر توضیح می‌دهد و دوباره می‌کوشد.
 * هر لمس یک قرصِ کوتاه کنارِ دکمه می‌گذارد که حالتِ تازه را با کلمه می‌گوید.
 *
 * با پورتال روی body می‌نشیند (position: fixed)، هر جا که App رندرش کند؛ کارت
 * پرده ندارد تا بازی زیرش بایستد نه.
 *
 * **همیشه پیداست** سرِ میزِ آنلاین، نه فقط وقتی صدا زنده است (خواسته‌ی کاربر،
 * سپتامبر ۲۰۲۶): تا وصل نشده، این میز صدا ندارد (صفِ سریع)، وصل نشد، یا
 * حرف‌زدنم بسته است، دکمه **خاکستری و غیرفعال** می‌مانَد و لمسش فقط می‌گوید
 * چرا (و اگر وصل نشده بود، دوباره می‌کوشد). پیش‌تر در همه‌ی این حالت‌ها
 * ناپدید می‌شد و بازیکن نمی‌فهمید صدا اصلاً هست.
 *
 * **سرِ میزِ مافیا** بازی نوبت می‌دهد ([[voice]].turn): در نوبتِ حرفِ خودم دکمه حلقه‌ی
 * طلاییِ تپنده دارد و «نوبتِ توست» می‌گوید؛ شب و نوبتِ دیگران خاکستری است و لمسش
 * می‌گوید «بازی بسته»، نه «صدایت بسته شده» (که مالِ میزبان و پنل است). آغاز و پایانِ
 * نوبتِ من و آغازِ شب خودشان قرصِ کوتاهی می‌گذارند.
 */
import { useEffect, useRef, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { useSignal, signal } from "../net/signal";
import { voice, type VoiceTurn } from "../net/voice";
import { client } from "../net/client";

const GREEN = "#2e9e55", RED = "#b3443a", GREY = "#5d587a", GOLD = "#c9a24d", TEAL = "#3fd6a0", INK = "#130c33", CREAM = "#fdf4e2";
const SIZE = 56;          // ۵۶px — بالای کفِ ۴۸ ِ لمس
const EDGE = 16;

/**
 * بازی‌ای که پرسشِ ناگهانی را برنمی‌تابد — مسابقه‌ی فلش‌ها، که کارتی روی تخته وسطِ مسابقه یعنی باخت — تا وقتی
 * true است پرسشِ بارِ اول را نگه می‌دارد. دکمه خودش کار می‌کند؛ کارت بعد از مسابقه (پنلِ نتیجه) می‌آید.
 */
export const voiceHold = signal(false);

export function VoiceButton() {
  const phase = useSignal(voice.phase);
  const canSpeak = useSignal(voice.canSpeak);
  const consent = useSignal(voice.consent);
  const muted = useSignal(voice.muted);
  const micOn = useSignal(voice.micOn);
  const opening = useSignal(voice.opening);
  const denied = useSignal(voice.denied);
  const ask = useSignal(voice.ask);
  const hold = useSignal(voiceHold);
  const speaking = useSignal(voice.speaking);
  const turn = useSignal(voice.turn);
  const [card, setCard] = useState<"" | "ask" | "denied">("");
  const [note, setNote] = useState("");
  const noteTimer = useRef<number | undefined>(undefined);
  const lastTurn = useRef<VoiceTurn>(turn);

  const flash = (text: string, ms = 1900) => {
    setNote(text);
    window.clearTimeout(noteTimer.current);
    noteTimer.current = window.setTimeout(() => setNote(""), ms);
  };

  // بارِ اول سرِ میزِ صدادار: خودش می‌پرسد.
  useEffect(() => { if (ask && !card && !hold) setCard("ask"); }, [ask, card, hold]);
  useEffect(() => { if (phase !== "live") setCard(""); }, [phase]);
  useEffect(() => () => window.clearTimeout(noteTimer.current), []);
  // نوبتِ مافیا: آغاز و پایانِ نوبتِ من، و آغازِ شب — بی لمس، تا بداند کی حرف بزند.
  useEffect(() => {
    const was = lastTurn.current;
    lastTurn.current = turn;
    if (turn === was || phase !== "live") return;
    if (turn === "mine") {
      flash(consent !== "yes" || denied ? "نوبت توست — دکمه را بزن"
        : muted ? "نوبت توست — میکروفونت بی‌صداست" : "نوبت توست — حرف بزن", 3200);
    } else if (turn === "night") {
      flash("شب شد — صدای همه بسته است", 2400);
    } else if (was === "mine") {
      flash("نوبتت تمام شد", 2400);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [turn, phase]);
  const covered = useChatSheetOpen(phase !== "off");

  // برگه‌ی چتِ میز از پایینِ صفحه بالا می‌آید و دکمه (پورتال روی body، بالای
  // لایه‌های App) گوشه‌ی پایین-چپش — همان «بفرست» ِ نوارِ نوشتن — را می‌پوشاند.
  // تا برگه باز است دکمه کنار می‌رود؛ پرسشِ اجازه هم بعدش دوباره پیدا می‌شود.
  // پنجره‌های وسطِ صفحه (حکم، پایانِ دست) کاری به این گوشه ندارند؛ ‎.veil هم بالاتر است.
  if (phase === "off" || covered) return null;

  // بازی بسته (مافیا: شب، یا نوبتِ دیگری). جدا از بستنِ میزبان و پنل.
  const byGame = !canSpeak && (turn === "night" || turn === "other");

  const tap = () => {
    if (phase === "failed") { voice.retry(); flash("صدا وصل نشد — دوباره وصل می‌شویم…"); return; }
    if (phase === "none") { flash("صدای زنده فقط در میزهای اتاق دوستان فعال است"); return; }
    if (phase !== "live") { flash("در حال وصل‌شدن به صدای میز…"); return; }
    if (byGame) {
      // هنوز «بله» نگفته؟ همین حالا، تا نوبتش که رسید میکروفون آماده باشد.
      if (denied) { setCard("denied"); return; }
      if (consent !== "yes") { setCard("ask"); return; }
      flash(turn === "night" ? "شب است — صدای همه بسته است" : "نوبت تو نیست — در نوبتت باز می‌شود", 2400);
      return;
    }
    if (!canSpeak) { flash("صدایت در این میز بسته شده"); return; }
    if (denied) { setCard("denied"); return; }
    if (consent !== "yes") { setCard("ask"); return; }
    voice.toggleMute();
    flash(voice.muted.value ? "بی‌صدا — کسی صدایت را نمی‌شنود" : "میکروفون باز است");
  };

  const firstAsk = ask && card === "ask";
  const closeCard = () => { if (firstAsk) voice.skipAsk(); setCard(""); };
  const allow = () => { setCard(""); voice.accept(); };
  const notNow = () => { if (firstAsk) voice.decline(); setCard(""); };

  const ready = phase === "live" && canSpeak;
  const live = ready && (micOn || opening);
  const myTurn = ready && turn === "mine";
  const talkingNow = micOn && speaking.has(client.playerId.value);
  const state = phase === "none" ? "این میز صدای زنده ندارد" : phase === "failed" ? "وصل نشد"
    : phase !== "live" ? "در حال وصل‌شدن" : byGame ? (turn === "night" ? "شب، بازی بسته" : "بازی بسته تا نوبتت")
    : !canSpeak ? "بسته"
    : (myTurn ? "نوبت توست، " : "") + (live ? "باز" : muted ? "بی‌صدا" : denied ? "بی‌اجازه‌ی مرورگر" : "خاموش");
  const label = `میکروفون: ${state}`;

  const btn: CSSProperties = {
    position: "fixed", left: `max(${EDGE}px, env(safe-area-inset-left))`, bottom: `max(${EDGE}px, env(safe-area-inset-bottom))`,
    zIndex: 45, inlineSize: SIZE, blockSize: SIZE, borderRadius: "50%", padding: 0, cursor: "pointer",
    border: `3px solid ${ready ? GOLD : GOLD + "88"}`, background: !ready ? GREY : live ? GREEN : RED, color: "#fff",
    display: "grid", placeItems: "center", opacity: !ready ? 0.6 : opening ? 0.75 : 1,
    boxShadow: talkingNow ? `0 0 0 3px ${TEAL}88, 0 0 22px ${TEAL}cc, 0 4px 12px #0008`
      : myTurn ? `0 0 0 3px ${GOLD}aa, 0 0 20px ${GOLD}cc, 0 4px 12px #0008` : "0 4px 12px #0008",
    transition: "background .15s ease, box-shadow .2s ease, opacity .2s ease",
    touchAction: "manipulation", WebkitTapHighlightColor: "transparent",
  };
  const bubble: CSSProperties = {
    position: "fixed", left: `calc(max(${EDGE}px, env(safe-area-inset-left)) + ${SIZE + 12}px)`,
    bottom: `calc(max(${EDGE}px, env(safe-area-inset-bottom)) + ${SIZE / 2 - 16}px)`, zIndex: 45,
    background: `${INK}ee`, color: CREAM, fontSize: 13, lineHeight: "32px", padding: "0 14px",
    borderRadius: 999, whiteSpace: "nowrap", boxShadow: "0 3px 10px #0008", pointerEvents: "none",
  };
  const cardStyle: CSSProperties = {
    position: "fixed", left: `max(${EDGE}px, env(safe-area-inset-left))`,
    bottom: `calc(max(${EDGE}px, env(safe-area-inset-bottom)) + ${SIZE + 14}px)`, zIndex: 46,
    inlineSize: `min(360px, calc(100vw - ${EDGE * 2}px))`, boxSizing: "border-box",
    background: "linear-gradient(180deg, #342c70, #1e1a4c)", color: CREAM,
    border: `2px solid ${GOLD}99`, borderRadius: 18, padding: "16px 16px 14px",
    boxShadow: "0 14px 34px #000b", direction: "rtl", textAlign: "start",
  };
  const primary: CSSProperties = {
    flex: 1, minBlockSize: 48, borderRadius: 14, border: 0, cursor: "pointer", fontSize: 15, fontWeight: 700,
    background: "linear-gradient(180deg, #fbe6a3, #d9a95a)", color: "#3a2410",
  };
  const secondary: CSSProperties = {
    minInlineSize: 110, minBlockSize: 48, borderRadius: 14, cursor: "pointer", fontSize: 14,
    background: "#ffffff14", color: CREAM, border: `1px solid ${GOLD}66`,
  };

  return createPortal(
    <>
      <button type="button" style={btn} onClick={tap} aria-label={label} title={label}
              aria-pressed={ready ? !live : undefined} aria-disabled={!ready}
              className={myTurn && !talkingNow ? "voice-mute voice-turn" : "voice-mute"}>
        <svg viewBox="0 0 24 24" width="28" height="28" aria-hidden="true" fill="none" stroke="currentColor"
             strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="9" y="2.5" width="6" height="12" rx="3" />
          <path d="M5 11a7 7 0 0 0 14 0M12 18v3.5" />
          {!live && <path d="M4 4l16 16" strokeWidth="2.6" />}
        </svg>
      </button>
      {note && !card && <div style={bubble} role="status" aria-live="polite">{note}</div>}
      {card && (
        <div style={cardStyle} role="dialog" aria-modal="false"
             aria-label={card === "denied" ? "میکروفون بسته است" : "صدای زنده‌ی میز"}
             onKeyDown={(e) => { if (e.key === "Escape") closeCard(); }}>
          <button type="button" onClick={closeCard} aria-label="بستن"
                  style={{ position: "absolute", insetInlineEnd: 6, insetBlockStart: 6, inlineSize: 44, blockSize: 44,
                           border: 0, background: "transparent", color: `${CREAM}aa`, fontSize: 22, cursor: "pointer" }}>×</button>
          <div style={{ fontSize: 17, fontWeight: 700, color: "#e9cd7e", marginInlineEnd: 40 }}>
            {card === "denied" ? "میکروفون بسته است" : "صدای زنده‌ی میز"}
          </div>
          <p style={{ margin: "10px 0 14px", fontSize: 14, lineHeight: 1.8 }}>
            {card === "denied"
              ? "مرورگر اجازه‌ی میکروفون نداده. روی نماد قفل کنار آدرس صفحه بزن، میکروفون را «اجازه» بده و دوباره امتحان کن."
              : turn
              ? "مافیا با صدای زنده: میکروفونت فقط در نوبت حرف خودت باز می‌شود و شب صدای همه بسته است. صدای بقیه را همیشه می‌شنوی."
              : "با دوستانت سر میز حرف بزن: میکروفون تا پایان بازی باز می‌ماند و صدای بقیه پخش می‌شود. هر وقت خواستی با همین دکمه‌ی پایین-چپ بی‌صدا کن."}
          </p>
          <div style={{ display: "flex", gap: 10 }}>
            <button type="button" style={primary} onClick={allow} autoFocus>
              {card === "denied" ? "دوباره امتحان کن" : "اجازه می‌دهم"}
            </button>
            <button type="button" style={secondary} onClick={card === "denied" ? closeCard : notNow}>
              {card === "denied" ? "بستن" : "فعلاً نه"}
            </button>
          </div>
        </div>
      )}
    </>,
    document.body,
  );
}

/** برگه‌ی چتِ میز (‎.chatwrap) باز است؟ فقط وقتی `on`. */
function useChatSheetOpen(on: boolean): boolean {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!on) { setOpen(false); return; }
    // برگه‌ی ⚙ ِ میز (‎.tset) هم از پایین بالا می‌آید و «ادامه‌ی بازی» اش همین گوشه است.
    const check = () => setOpen(!!document.querySelector(".chatwrap, .tset"));
    check();
    const mo = new MutationObserver(check);
    mo.observe(document.body, { childList: true, subtree: true });
    return () => mo.disconnect();
  }, [on]);
  return open;
}
